
import React, { useEffect, useMemo, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import AppDialog from "../components/AppDialog";
import { formatApiError } from "../lib/formatApiError";

interface DraftBoard {
  id: string;
  name: string;
  status: string;
  ageGroup?: string | null;
  notes?: string | null;
  organizationId: string;
  seasonId: string;
  programId?: string | null;
}

interface DraftAssignment {
  id: string;
  draftBoardId: string;
  teamId: string;
  drafteeId: string;
  playerId?: string | null;
  rosterMembershipId?: string | null;
  notes?: string | null;
}

interface Draftee {
  id: string;
  draftBoardId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  town?: string | null;
  state?: string | null;
  parentEmail?: string | null;
  batting?: number | null;
  fielding?: number | null;
  throwing?: number | null;
  baserunning?: number | null;
  coachability?: number | null;
  availabilityRating?: number | null;
  catcher?: boolean | null;
  pitcher?: boolean | null;
  finalizedPlayerId?: string | null;
}

interface Organization { id: string; name: string; }
interface Season { id: string; name: string; year: number; }
interface Program { id: string; name: string; type: string; organizationId: string; seasonId?: string | null; }
interface Team { id: string; name: string; ageGroup?: string | null; organizationId?: string | null; programId?: string | null; seasonId?: string | null; }
interface RosterMembership { id: string; playerId: string; teamId: string; seasonId: string; organizationId?: string | null; programId?: string | null; relationship: string; isPrimary: boolean; }
interface Player { id: string; name: string; teamId: string; }
interface TeamPlayer { id: string; teamID: string; playerID: string; }
interface DraftsPageProps { userRoles: string[]; }
interface HoveredDrafteeState {
  drafteeId: string;
  top: number;
  left: number;
  placement: "left" | "right";
}
type AutoDraftMode = "BALANCE" | "TIERED";
type TierRank = "A" | "B" | "C" | "D";
interface TeamDraftLoad {
  teamId: string;
  size: number;
  score: number;
  pitcherCount: number;
  catcherCount: number;
  drafteeIds: string[];
}

type SortKey = "name" | "organization" | "program" | "season" | "ageGroup" | "status";

type DrafteeSortKey = "name" | "dob" | "town" | "state" | "availability";

const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  out.push(current.trim());
  return out;
};

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, "");
const normalizeAgeGroup = (value?: string | null) => (value ?? "").trim().toUpperCase();
const coerceOptionalInt = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
};
const coerceBoolean = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "y";
};
const drafteeFullName = (draftee: Draftee) => `${draftee.firstName} ${draftee.lastName}`.trim();
const numericRatingsForDraftee = (draftee: Draftee) => [draftee.batting, draftee.fielding, draftee.throwing, draftee.baserunning, draftee.coachability, draftee.availabilityRating].filter((value): value is number => typeof value === "number" && Number.isFinite(value));
const averageRatingForDraftee = (draftee: Draftee) => {
  const values = numericRatingsForDraftee(draftee);
  if (values.length === 0) {
    return 50;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};
const pitcherStrengthForDraftee = (draftee: Draftee) => {
  if (!draftee.pitcher) {
    return 0;
  }
  return (draftee.throwing ?? averageRatingForDraftee(draftee)) * 1.2 + (draftee.coachability ?? averageRatingForDraftee(draftee)) * 0.45;
};
const catcherStrengthForDraftee = (draftee: Draftee) => {
  if (!draftee.catcher) {
    return 0;
  }
  return (draftee.fielding ?? averageRatingForDraftee(draftee)) * 1.1 + (draftee.coachability ?? averageRatingForDraftee(draftee)) * 0.4;
};
const compositeDraftScore = (draftee: Draftee) => (averageRatingForDraftee(draftee) * 10) + pitcherStrengthForDraftee(draftee) + catcherStrengthForDraftee(draftee);
const averageCategoryForDraftees = (draftees: Draftee[], selector: (draftee: Draftee) => number | null | undefined) => {
  const values = draftees.map(selector).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};
const tierRankOrder: TierRank[] = ["A", "B", "C", "D"];
const normalizeTierRank = (value?: string | null): TierRank => {
  const normalized = (value ?? "").trim().toUpperCase();
  if (normalized === "A" || normalized === "B" || normalized === "C" || normalized === "D") {
    return normalized;
  }
  return "B";
};
const createInitialTeamLoad = (teamId: string): TeamDraftLoad => ({
  teamId,
  size: 0,
  score: 0,
  pitcherCount: 0,
  catcherCount: 0,
  drafteeIds: []
});
const cloneTeamLoad = (load: TeamDraftLoad): TeamDraftLoad => ({
  teamId: load.teamId,
  size: load.size,
  score: load.score,
  pitcherCount: load.pitcherCount,
  catcherCount: load.catcherCount,
  drafteeIds: [...load.drafteeIds]
});
const applyDrafteeToLoad = (load: TeamDraftLoad, draftee: Draftee) => {
  load.size += 1;
  load.score += compositeDraftScore(draftee);
  load.pitcherCount += draftee.pitcher ? 1 : 0;
  load.catcherCount += draftee.catcher ? 1 : 0;
  load.drafteeIds.push(draftee.id);
};
const selectBestTeamLoad = (loads: TeamDraftLoad[], draftee: Draftee, tieBreaker: Record<string, number>) => {
  const totalTeams = Math.max(loads.length, 1);
  let best = loads[0];
  let bestScore = Number.POSITIVE_INFINITY;
  for (const load of loads) {
    const predictedSize = load.size + 1;
    const predictedScore = load.score + compositeDraftScore(draftee);
    const predictedPitchers = load.pitcherCount + (draftee.pitcher ? 1 : 0);
    const predictedCatchers = load.catcherCount + (draftee.catcher ? 1 : 0);
    const sizePenalty = predictedSize * 280;
    const scorePenalty = predictedScore;
    const pitcherPenalty = draftee.pitcher ? predictedPitchers * 120 : load.pitcherCount * 24;
    const catcherPenalty = draftee.catcher ? predictedCatchers * 95 : load.catcherCount * 18;
    const tiePenalty = tieBreaker[load.teamId] ?? 0;
    const value = sizePenalty + scorePenalty + pitcherPenalty + catcherPenalty + tiePenalty + ((load.pitcherCount + load.catcherCount) / totalTeams);
    if (value < bestScore) {
      best = load;
      bestScore = value;
    }
  }
  return best;
};
const distributeBalancedAssignments = (draftees: Draftee[], baseLoads: TeamDraftLoad[], tieBreaker: Record<string, number>) => {
  const workingLoads = baseLoads.map(cloneTeamLoad);
  const draftPlan: Array<{ teamId: string; drafteeId: string }> = [];
  const sortedDraftees = [...draftees].sort((a, b) => compositeDraftScore(b) - compositeDraftScore(a));
  for (const draftee of sortedDraftees) {
    const bestLoad = selectBestTeamLoad(workingLoads, draftee, tieBreaker);
    applyDrafteeToLoad(bestLoad, draftee);
    draftPlan.push({ teamId: bestLoad.teamId, drafteeId: draftee.id });
  }
  return draftPlan;
};
const allocateTierSlots = (totalPlayers: number, tierEntries: Array<{ tier: TierRank; teamCount: number }>) => {
  const totalTeams = tierEntries.reduce((sum, entry) => sum + entry.teamCount, 0);
  const base = tierEntries.map((entry) => {
    const exact = totalTeams === 0 ? 0 : (totalPlayers * entry.teamCount) / totalTeams;
    return {
      tier: entry.tier,
      teamCount: entry.teamCount,
      slots: Math.floor(exact),
      remainder: exact - Math.floor(exact)
    };
  });
  let assigned = base.reduce((sum, entry) => sum + entry.slots, 0);
  const ordered = [...base].sort((a, b) => b.remainder - a.remainder || tierRankOrder.indexOf(a.tier) - tierRankOrder.indexOf(b.tier));
  let cursor = 0;
  while (assigned < totalPlayers && ordered.length > 0) {
    ordered[cursor % ordered.length].slots += 1;
    assigned += 1;
    cursor += 1;
  }
  return base.reduce<Record<TierRank, number>>((acc, entry) => {
    acc[entry.tier] = entry.slots;
    return acc;
  }, { A: 0, B: 0, C: 0, D: 0 });
};
const allocateTierRoleTargets = (
  totalRolePlayers: number,
  teamsByTier: Record<TierRank, Team[]>,
  baseLoadsByTeamId: Record<string, TeamDraftLoad>,
  roleKey: "pitcherCount" | "catcherCount"
) => {
  const allTeams = tierRankOrder.flatMap((tier) => teamsByTier[tier]);
  const activeTiers = tierRankOrder.filter((tier) => teamsByTier[tier].length > 0);
  const teamCount = allTeams.length;
  const existingRoleTotal = allTeams.reduce((sum, team) => sum + (baseLoadsByTeamId[team.id]?.[roleKey] ?? 0), 0);
  const totalRoleCapacity = existingRoleTotal + totalRolePlayers;
  const baseMinimum = teamCount === 0 ? 0 : Math.min(2, Math.floor(totalRoleCapacity / teamCount));
  const targets = allTeams.reduce<Record<string, number>>((acc, team) => {
    const existing = baseLoadsByTeamId[team.id]?.[roleKey] ?? 0;
    acc[team.id] = Math.max(existing, baseMinimum);
    return acc;
  }, {});
  let assignedTargets = Object.values(targets).reduce((sum, value) => sum + value, 0);
  let extrasToDistribute = Math.max(0, totalRoleCapacity - assignedTargets);
  const tierTeamCursor = activeTiers.reduce<Record<string, number>>((acc, tier) => {
    acc[tier] = 0;
    return acc;
  }, {});
  while (extrasToDistribute > 0 && activeTiers.length > 0) {
    for (let tierIndex = 0; tierIndex < activeTiers.length && extrasToDistribute > 0; tierIndex += 1) {
      const tier = activeTiers[tierIndex];
      const teamsInTier = teamsByTier[tier];
      const tierWeight = activeTiers.length - tierIndex;
      for (let weightIndex = 0; weightIndex < tierWeight && extrasToDistribute > 0; weightIndex += 1) {
        const team = teamsInTier[tierTeamCursor[tier] % teamsInTier.length];
        tierTeamCursor[tier] += 1;
        targets[team.id] += 1;
        extrasToDistribute -= 1;
      }
    }
  }
  return targets;
};
const assignTierRolePlayers = (
  rolePlayers: Draftee[],
  roleKey: "pitcherCount" | "catcherCount",
  loadsByTeamId: Record<string, TeamDraftLoad>,
  teamsByTier: Record<TierRank, Team[]>,
  targets: Record<string, number>,
  scoreByTeamId: Record<string, number>,
  assignPlayerToTeam: (draftee: Draftee, teamId: string) => void,
  assignedIds: Set<string>
) => {
  let playerIndex = 0;
  for (const tier of tierRankOrder) {
    const tierTeams = teamsByTier[tier].filter((team) => loadsByTeamId[team.id]);
    if (tierTeams.length === 0) {
      continue;
    }
    while (playerIndex < rolePlayers.length) {
      const candidates = tierTeams
        .map((team) => ({
          team,
          shortage: (targets[team.id] ?? 0) - (loadsByTeamId[team.id]?.[roleKey] ?? 0),
          currentSize: loadsByTeamId[team.id]?.size ?? 0,
          currentScore: scoreByTeamId[team.id] ?? 0,
          currentRoleCount: loadsByTeamId[team.id]?.[roleKey] ?? 0
        }))
        .filter((entry) => entry.shortage > 0)
        .sort((a, b) => {
          if (b.shortage !== a.shortage) return b.shortage - a.shortage;
          if (a.currentRoleCount !== b.currentRoleCount) return a.currentRoleCount - b.currentRoleCount;
          if (a.currentSize !== b.currentSize) return a.currentSize - b.currentSize;
          return a.currentScore - b.currentScore;
        });
      if (candidates.length === 0) {
        break;
      }
      const nextPlayer = rolePlayers[playerIndex];
      if (!nextPlayer || assignedIds.has(nextPlayer.id)) {
        playerIndex += 1;
        continue;
      }
      assignPlayerToTeam(nextPlayer, candidates[0].team.id);
      playerIndex += 1;
    }
  }
};
const distributeTieredAssignments = (
  draftees: Draftee[],
  baseLoads: TeamDraftLoad[],
  teamsByTier: Record<TierRank, Team[]>
) => {
  const loadsByTeamId = baseLoads.reduce<Record<string, TeamDraftLoad>>((acc, load) => {
    acc[load.teamId] = cloneTeamLoad(load);
    return acc;
  }, {});
  const plan: Array<{ teamId: string; drafteeId: string }> = [];
  const assignedIds = new Set<string>();
  const scoreByTeamId = Object.values(loadsByTeamId).reduce<Record<string, number>>((acc, load) => {
    acc[load.teamId] = load.score;
    return acc;
  }, {});
  const tierEntries = tierRankOrder.map((tier) => ({ tier, teamCount: teamsByTier[tier].length })).filter((entry) => entry.teamCount > 0);
  const totalTierSlots = allocateTierSlots(draftees.length, tierEntries);

  const assignPlayerToTeam = (draftee: Draftee, teamId: string) => {
    const load = loadsByTeamId[teamId];
    if (!load || assignedIds.has(draftee.id)) {
      return;
    }
    applyDrafteeToLoad(load, draftee);
    scoreByTeamId[teamId] = load.score;
    assignedIds.add(draftee.id);
    plan.push({ teamId, drafteeId: draftee.id });
  };

  const availablePitchers = draftees.filter((item) => item.pitcher).sort((a, b) => pitcherStrengthForDraftee(b) - pitcherStrengthForDraftee(a));
  const pitcherTargets = allocateTierRoleTargets(availablePitchers.length, teamsByTier, loadsByTeamId, "pitcherCount");
  assignTierRolePlayers(availablePitchers, "pitcherCount", loadsByTeamId, teamsByTier, pitcherTargets, scoreByTeamId, assignPlayerToTeam, assignedIds);

  const availableCatchers = draftees.filter((item) => item.catcher && !assignedIds.has(item.id)).sort((a, b) => catcherStrengthForDraftee(b) - catcherStrengthForDraftee(a));
  const catcherTargets = allocateTierRoleTargets(
    availableCatchers.length,
    teamsByTier,
    loadsByTeamId,
    "catcherCount"
  );
  assignTierRolePlayers(availableCatchers, "catcherCount", loadsByTeamId, teamsByTier, catcherTargets, scoreByTeamId, assignPlayerToTeam, assignedIds);

  const remainingPlayers = draftees.filter((item) => !assignedIds.has(item.id)).sort((a, b) => compositeDraftScore(b) - compositeDraftScore(a));
  const assignedTierCounts = tierRankOrder.reduce<Record<TierRank, number>>((acc, tier) => {
    acc[tier] = teamsByTier[tier].reduce((sum, team) => sum + (loadsByTeamId[team.id]?.size ?? 0), 0);
    return acc;
  }, { A: 0, B: 0, C: 0, D: 0 });
  const remainingTierSlots = tierRankOrder.reduce<Record<TierRank, number>>((acc, tier) => {
    acc[tier] = Math.max(0, totalTierSlots[tier] - assignedTierCounts[tier]);
    return acc;
  }, { A: 0, B: 0, C: 0, D: 0 });
  let cursor = 0;
  for (const tier of tierRankOrder) {
    const tierTeams = teamsByTier[tier];
    if (tierTeams.length === 0) {
      continue;
    }
    const slice = remainingPlayers.slice(cursor, cursor + remainingTierSlots[tier]);
    cursor += remainingTierSlots[tier];
    const tierTieBreaker = tierTeams.reduce<Record<string, number>>((acc, team, index) => {
      acc[team.id] = index / 1000;
      return acc;
    }, {});
    const tierPlan = distributeBalancedAssignments(slice, tierTeams.map((team) => loadsByTeamId[team.id] ?? createInitialTeamLoad(team.id)), tierTieBreaker);
    for (const item of tierPlan) {
      const draftee = draftees.find((entry) => entry.id === item.drafteeId);
      if (!draftee || assignedIds.has(draftee.id)) {
        continue;
      }
      assignPlayerToTeam(draftee, item.teamId);
    }
  }

  return plan;
};
const normalizeDateForApi = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(trimmed)) {
    const [monthRaw, dayRaw, yearRaw] = trimmed.split("/");
    const month = Number.parseInt(monthRaw, 10);
    const day = Number.parseInt(dayRaw, 10);
    let year = Number.parseInt(yearRaw, 10);
    if (yearRaw.length === 2) {
      year += year >= 50 ? 1900 : 2000;
    }
    if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) {
      return null;
    }
    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (candidate.getUTCFullYear() !== year || candidate.getUTCMonth() !== month - 1 || candidate.getUTCDate() !== day) {
      return null;
    }
    return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  }
  if (/^\d{5}(?:\.\d+)?$/.test(trimmed)) {
    const serial = Number.parseFloat(trimmed);
    if (!Number.isFinite(serial)) {
      return null;
    }
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    excelEpoch.setUTCDate(excelEpoch.getUTCDate() + Math.floor(serial));
    const year = excelEpoch.getUTCFullYear();
    const month = (excelEpoch.getUTCMonth() + 1).toString().padStart(2, "0");
    const day = excelEpoch.getUTCDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const year = parsed.getUTCFullYear();
  const month = (parsed.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = parsed.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const computeAgeFromDob = (dateOfBirth?: string | null): number | null => {
  if (!dateOfBirth) {
    return null;
  }
  const dob = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const beforeBirthday = now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) {
    age -= 1;
  }
  return Math.max(0, age);
};

const listDraftBoardsQuery = /* GraphQL */ `query ListDraftBoardsForPage($limit: Int, $nextToken: String) { listDraftBoards(limit: $limit, nextToken: $nextToken) { items { id name status ageGroup notes organizationId seasonId programId } nextToken } }`;
const listDraftAssignmentsQuery = /* GraphQL */ `query ListDraftAssignmentsForPage($limit: Int, $nextToken: String) { listDraftAssignments(limit: $limit, nextToken: $nextToken) { items { id draftBoardId teamId drafteeId playerId rosterMembershipId notes } nextToken } }`;
const listDrafteesQuery = /* GraphQL */ `query ListDrafteesForPage($limit: Int, $nextToken: String) { listDraftees(limit: $limit, nextToken: $nextToken) { items { id draftBoardId firstName lastName dateOfBirth town state parentEmail batting fielding throwing baserunning coachability availabilityRating catcher pitcher finalizedPlayerId } nextToken } }`;
const listTeamsQuery = /* GraphQL */ `query ListTeamsForDraftBoard($limit: Int, $nextToken: String) { listTeams(limit: $limit, nextToken: $nextToken) { items { id name ageGroup organizationId programId seasonId } nextToken } }`;
const listOrganizationsQuery = /* GraphQL */ `query ListOrganizationsForDraftBoard($limit: Int, $nextToken: String) { listOrganizations(limit: $limit, nextToken: $nextToken) { items { id name } nextToken } }`;
const listProgramsQuery = /* GraphQL */ `query ListProgramsForDraftBoard($limit: Int, $nextToken: String) { listPrograms(limit: $limit, nextToken: $nextToken) { items { id name type organizationId seasonId } nextToken } }`;
const listSeasonsQuery = /* GraphQL */ `query ListSeasonsForDraftBoard($limit: Int, $nextToken: String) { listSeasons(limit: $limit, nextToken: $nextToken) { items { id name year } nextToken } }`;
const listRosterMembershipsQuery = /* GraphQL */ `query ListRosterMembershipsForDraftBoard($limit: Int, $nextToken: String) { listRosterMemberships(limit: $limit, nextToken: $nextToken) { items { id playerId teamId seasonId organizationId programId relationship isPrimary } nextToken } }`;
const listPlayersQuery = /* GraphQL */ `query ListPlayersForDraftBoard($limit: Int, $nextToken: String) { listPlayers(limit: $limit, nextToken: $nextToken) { items { id name teamId } nextToken } }`;
const listTeamPlayersQuery = /* GraphQL */ `query ListTeamPlayersForDraftBoard($limit: Int, $nextToken: String) { listTeamPlayers(limit: $limit, nextToken: $nextToken) { items { id teamID playerID } nextToken } }`;
const createDraftBoardMutation = /* GraphQL */ `mutation CreateDraftBoardForPage($input: CreateDraftBoardInput!) { createDraftBoard(input: $input) { id name status ageGroup notes organizationId seasonId programId } }`;
const updateDraftBoardMutation = /* GraphQL */ `mutation UpdateDraftBoardForPage($input: UpdateDraftBoardInput!) { updateDraftBoard(input: $input) { id name status ageGroup notes organizationId seasonId programId } }`;
const deleteDraftBoardMutation = /* GraphQL */ `mutation DeleteDraftBoardForPage($input: DeleteDraftBoardInput!) { deleteDraftBoard(input: $input) { id } }`;
const createDrafteeMutation = /* GraphQL */ `mutation CreateDrafteeForPage($input: CreateDrafteeInput!) { createDraftee(input: $input) { id draftBoardId firstName lastName dateOfBirth town state parentEmail batting fielding throwing baserunning coachability availabilityRating catcher pitcher finalizedPlayerId } }`;
const updateDrafteeMutation = /* GraphQL */ `mutation UpdateDrafteeForPage($input: UpdateDrafteeInput!) { updateDraftee(input: $input) { id finalizedPlayerId } }`;
const deleteDrafteeMutation = /* GraphQL */ `mutation DeleteDrafteeForPage($input: DeleteDrafteeInput!) { deleteDraftee(input: $input) { id } }`;
const createDraftAssignmentMutation = /* GraphQL */ `mutation CreateDraftAssignmentForPage($input: CreateDraftAssignmentInput!) { createDraftAssignment(input: $input) { id draftBoardId teamId drafteeId playerId rosterMembershipId notes } }`;
const deleteDraftAssignmentMutation = /* GraphQL */ `mutation DeleteDraftAssignmentForPage($input: DeleteDraftAssignmentInput!) { deleteDraftAssignment(input: $input) { id } }`;
const createPlayerMutation = /* GraphQL */ `mutation CreatePlayerFromDraft($input: CreatePlayerInput!) { createPlayer(input: $input) { id name teamId } }`;
const createPlayerParentMutation = /* GraphQL */ `mutation CreatePlayerParentFromDraft($input: CreatePlayerParentInput!) { createPlayerParent(input: $input) { id playerId parentEmail } }`;
const createTeamPlayerMutation = /* GraphQL */ `mutation CreateTeamPlayerFromDraft($input: CreateTeamPlayerInput!) { createTeamPlayer(input: $input) { id teamID playerID } }`;
const createRosterMembershipMutation = /* GraphQL */ `mutation CreateRosterMembershipForDraftBoard($input: CreateRosterMembershipInput!) { createRosterMembership(input: $input) { id playerId teamId seasonId organizationId programId relationship isPrimary } }`;

const DraftsPage: React.FC<DraftsPageProps> = ({ userRoles }) => {
  const canManage = userRoles.includes("SuperAdmin") || userRoles.includes("Commish");
  const [draftBoards, setDraftBoards] = useState<DraftBoard[]>([]);
  const [assignments, setAssignments] = useState<DraftAssignment[]>([]);
  const [draftees, setDraftees] = useState<Draftee[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeDraftId, setActiveDraftId] = useState("");
  const [draggingDrafteeId, setDraggingDrafteeId] = useState<string | null>(null);
  const [draggingOverTeamId, setDraggingOverTeamId] = useState<string | null>(null);
  const [hoveredDraftee, setHoveredDraftee] = useState<HoveredDrafteeState | null>(null);
  const [bulkStatusMessage, setBulkStatusMessage] = useState("");
  const [bulkErrorMessage, setBulkErrorMessage] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [finalizingDraft, setFinalizingDraft] = useState(false);
  const [showAutoDraftDialog, setShowAutoDraftDialog] = useState(false);
  const [autoDraftMode, setAutoDraftMode] = useState<AutoDraftMode>("BALANCE");
  const [autoDraftLoading, setAutoDraftLoading] = useState(false);
  const [teamTierRanks, setTeamTierRanks] = useState<Record<string, TierRank>>({});

  const [filterText, setFilterText] = useState("");
  const [filterOrganizationId, setFilterOrganizationId] = useState("");
  const [filterSeasonId, setFilterSeasonId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("season");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [poolSearch, setPoolSearch] = useState("");
  const [poolAgeGroup, setPoolAgeGroup] = useState("");
  const [drafteeSortKey, setDrafteeSortKey] = useState<DrafteeSortKey>("name");
  const [drafteeSortDirection, setDrafteeSortDirection] = useState<"asc" | "desc">("asc");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState("PLANNING");
  const [newAgeGroup, setNewAgeGroup] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newOrganizationId, setNewOrganizationId] = useState("");
  const [newSeasonId, setNewSeasonId] = useState("");
  const [newProgramId, setNewProgramId] = useState("");
  const [editingDraftId, setEditingDraftId] = useState("");
  const [editingName, setEditingName] = useState("");
  const [editingStatus, setEditingStatus] = useState("PLANNING");
  const [editingAgeGroup, setEditingAgeGroup] = useState("");
  const [editingNotes, setEditingNotes] = useState("");
  const [editingOrganizationId, setEditingOrganizationId] = useState("");
  const [editingSeasonId, setEditingSeasonId] = useState("");
  const [editingProgramId, setEditingProgramId] = useState("");

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const [draftRes, assignmentRes, drafteeRes, organizationRes, programRes, seasonRes, teamRes, membershipRes, playerRes, teamPlayerRes] = await Promise.all([
        API.graphql(graphqlOperation(listDraftBoardsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listDraftAssignmentsQuery, { limit: 4000 })),
        API.graphql(graphqlOperation(listDrafteesQuery, { limit: 4000 })),
        API.graphql(graphqlOperation(listOrganizationsQuery, { limit: 300 })),
        API.graphql(graphqlOperation(listProgramsQuery, { limit: 400 })),
        API.graphql(graphqlOperation(listSeasonsQuery, { limit: 300 })),
        API.graphql(graphqlOperation(listTeamsQuery, { limit: 600 })),
        API.graphql(graphqlOperation(listRosterMembershipsQuery, { limit: 4000 })),
        API.graphql(graphqlOperation(listPlayersQuery, { limit: 3000 })),
        API.graphql(graphqlOperation(listTeamPlayersQuery, { limit: 5000 }))
      ]);

      setDraftBoards(((draftRes as any)?.data?.listDraftBoards?.items ?? []).filter(Boolean));
      setAssignments(((assignmentRes as any)?.data?.listDraftAssignments?.items ?? []).filter(Boolean));
      setDraftees(((drafteeRes as any)?.data?.listDraftees?.items ?? []).filter(Boolean));
      setOrganizations(((organizationRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean));
      setPrograms(((programRes as any)?.data?.listPrograms?.items ?? []).filter(Boolean));
      setSeasons(((seasonRes as any)?.data?.listSeasons?.items ?? []).filter(Boolean));
      setTeams(((teamRes as any)?.data?.listTeams?.items ?? []).filter(Boolean));
      setRosterMemberships(((membershipRes as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean));
      setPlayers(((playerRes as any)?.data?.listPlayers?.items ?? []).filter(Boolean));
      setTeamPlayers(((teamPlayerRes as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean));
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load drafts: ${formatApiError(error)}`);
      }
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!activeDraftId && draftBoards.length > 0) {
      setActiveDraftId(draftBoards[0].id);
      return;
    }
    if (activeDraftId && !draftBoards.some((draft) => draft.id === activeDraftId)) {
      setActiveDraftId(draftBoards[0]?.id ?? "");
    }
  }, [activeDraftId, draftBoards]);

  const organizationNameById = useMemo(() => organizations.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.id]: item.name }), {}), [organizations]);
  const seasonLabelById = useMemo(() => seasons.reduce<Record<string, string>>((acc, item) => { acc[item.id] = `${item.name} ${item.year}`; return acc; }, {}), [seasons]);
  const programNameById = useMemo(() => programs.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.id]: item.name }), {}), [programs]);
  const teamById = useMemo(() => teams.reduce<Record<string, Team>>((acc, item) => ({ ...acc, [item.id]: item }), {}), [teams]);
  const drafteeById = useMemo(() => draftees.reduce<Record<string, Draftee>>((acc, item) => ({ ...acc, [item.id]: item }), {}), [draftees]);

  const activeDraft = draftBoards.find((draft) => draft.id === activeDraftId) ?? null;
  const activeDraftees = useMemo(() => draftees.filter((item) => item.draftBoardId === activeDraftId), [activeDraftId, draftees]);
  const activeAssignments = useMemo(() => assignments.filter((assignment) => assignment.draftBoardId === activeDraftId), [activeDraftId, assignments]);
  const assignedDrafteeIds = useMemo(() => new Set(activeAssignments.map((assignment) => assignment.drafteeId)), [activeAssignments]);

  const filteredDrafts = useMemo(() => {
    const search = filterText.trim().toLowerCase();
    const rows = draftBoards.filter((draft) => {
      const haystack = [draft.name, draft.status, draft.ageGroup ?? "", organizationNameById[draft.organizationId] ?? "", programNameById[draft.programId ?? ""] ?? "", seasonLabelById[draft.seasonId] ?? ""].join(" ").toLowerCase();
      if (search && !haystack.includes(search)) return false;
      if (filterOrganizationId && draft.organizationId !== filterOrganizationId) return false;
      if (filterSeasonId && draft.seasonId !== filterSeasonId) return false;
      if (filterStatus && draft.status !== filterStatus) return false;
      return true;
    });
    return [...rows].sort((a, b) => {
      let compare = 0;
      if (sortKey === "organization") compare = (organizationNameById[a.organizationId] ?? "").localeCompare(organizationNameById[b.organizationId] ?? "");
      else if (sortKey === "program") compare = (programNameById[a.programId ?? ""] ?? "").localeCompare(programNameById[b.programId ?? ""] ?? "");
      else if (sortKey === "season") compare = (seasonLabelById[a.seasonId] ?? "").localeCompare(seasonLabelById[b.seasonId] ?? "");
      else if (sortKey === "ageGroup") compare = (a.ageGroup ?? "").localeCompare(b.ageGroup ?? "");
      else if (sortKey === "status") compare = a.status.localeCompare(b.status);
      else compare = a.name.localeCompare(b.name);
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [draftBoards, filterOrganizationId, filterSeasonId, filterStatus, filterText, organizationNameById, programNameById, seasonLabelById, sortDirection, sortKey]);

  const eligibleTeams = useMemo(() => {
    if (!activeDraft) return [];
    return teams.filter((team) => {
      if (team.organizationId !== activeDraft.organizationId) return false;
      if (activeDraft.seasonId && team.seasonId !== activeDraft.seasonId) return false;
      if (activeDraft.programId && team.programId !== activeDraft.programId) return false;
      if (normalizeAgeGroup(activeDraft.ageGroup) && normalizeAgeGroup(team.ageGroup) !== normalizeAgeGroup(activeDraft.ageGroup)) return false;
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [activeDraft, teams]);

  useEffect(() => {
    if (!showAutoDraftDialog) {
      return;
    }
    setTeamTierRanks((current) => {
      const next: Record<string, TierRank> = {};
      for (const team of eligibleTeams) {
        const existing = current[team.id];
        const inferred = normalizeTierRank(team.name.charAt(0));
        next[team.id] = existing ?? inferred;
      }
      return next;
    });
  }, [eligibleTeams, showAutoDraftDialog]);

  const teamAssignments = useMemo(() => {
    const map: Record<string, DraftAssignment[]> = {};
    for (const assignment of activeAssignments) {
      if (!map[assignment.teamId]) map[assignment.teamId] = [];
      map[assignment.teamId].push(assignment);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => drafteeFullName(drafteeById[a.drafteeId]).localeCompare(drafteeFullName(drafteeById[b.drafteeId])));
    }
    return map;
  }, [activeAssignments, drafteeById]);

  const currentPrograms = useMemo(() => {
    const orgId = createOpen ? newOrganizationId : editingOrganizationId;
    const seasonId = createOpen ? newSeasonId : editingSeasonId;
    return programs.filter((program) => {
      if (orgId && program.organizationId !== orgId) return false;
      if (seasonId && program.seasonId && program.seasonId !== seasonId) return false;
      return true;
    });
  }, [createOpen, editingOrganizationId, editingSeasonId, newOrganizationId, newSeasonId, programs]);

  const poolDraftees = useMemo(() => {
    const search = poolSearch.trim().toLowerCase();
    const rows = activeDraftees.filter((draftee) => {
      if (poolAgeGroup) { const computedAge = computeAgeFromDob(draftee.dateOfBirth); if (`${computedAge ?? ""}` !== poolAgeGroup.replace(/[^0-9]/g, "")) return false; }
      if (assignedDrafteeIds.has(draftee.id)) return false;
      if (search && !`${drafteeFullName(draftee)} ${draftee.town ?? ""} ${draftee.state ?? ""} ${draftee.parentEmail ?? ""}`.toLowerCase().includes(search)) return false;
      return true;
    });

    return [...rows].sort((a, b) => {
      let compare = 0;
      if (drafteeSortKey === "dob") compare = (a.dateOfBirth ?? "").localeCompare(b.dateOfBirth ?? "");
      else if (drafteeSortKey === "town") compare = (a.town ?? "").localeCompare(b.town ?? "");
      else if (drafteeSortKey === "state") compare = (a.state ?? "").localeCompare(b.state ?? "");
      else if (drafteeSortKey === "availability") compare = (a.availabilityRating ?? -1) - (b.availabilityRating ?? -1);
      else compare = drafteeFullName(a).localeCompare(drafteeFullName(b));
      return drafteeSortDirection === "asc" ? compare : -compare;
    });
  }, [activeDraftees, assignedDrafteeIds, drafteeSortDirection, drafteeSortKey, poolAgeGroup, poolSearch]);

  const finalizedCount = useMemo(() => activeDraftees.filter((item) => item.finalizedPlayerId).length, [activeDraftees]);
  const unfinalizedAssignedCount = useMemo(() => activeAssignments.filter((item) => !drafteeById[item.drafteeId]?.finalizedPlayerId).length, [activeAssignments, drafteeById]);

  const toggleSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) { setSortDirection((current) => (current === "asc" ? "desc" : "asc")); return; }
    setSortKey(nextKey);
    setSortDirection("asc");
  };

  const toggleDrafteeSort = (nextKey: DrafteeSortKey) => {
    if (drafteeSortKey === nextKey) { setDrafteeSortDirection((current) => (current === "asc" ? "desc" : "asc")); return; }
    setDrafteeSortKey(nextKey);
    setDrafteeSortDirection("asc");
  };

  const resetCreateState = () => {
    setNewName("");
    setNewStatus("PLANNING");
    setNewAgeGroup("");
    setNewNotes("");
    setNewOrganizationId("");
    setNewSeasonId("");
    setNewProgramId("");
  };

  const handleCreateDraft = async () => {
    if (!newName.trim() || !newOrganizationId || !newSeasonId) {
      setErrorMessage("Draft name, organization, and season are required.");
      return;
    }
    try {
      setErrorMessage("");
      const response = await API.graphql(graphqlOperation(createDraftBoardMutation, { input: { name: newName.trim(), status: newStatus, ageGroup: newAgeGroup.trim() || null, notes: newNotes.trim() || null, organizationId: newOrganizationId, seasonId: newSeasonId, programId: newProgramId || null } }));
      const created = (response as any)?.data?.createDraftBoard as DraftBoard | undefined;
      resetCreateState();
      setCreateOpen(false);
      await loadData(true);
      if (created?.id) setActiveDraftId(created.id);
    } catch (error) {
      setErrorMessage(`Failed to create draft: ${formatApiError(error)}`);
    }
  };

  const handleUpdateDraft = async () => {
    if (!editingDraftId || !editingName.trim() || !editingOrganizationId || !editingSeasonId) {
      setErrorMessage("Draft name, organization, and season are required.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(updateDraftBoardMutation, { input: { id: editingDraftId, name: editingName.trim(), status: editingStatus, ageGroup: editingAgeGroup.trim() || null, notes: editingNotes.trim() || null, organizationId: editingOrganizationId, seasonId: editingSeasonId, programId: editingProgramId || null } }));
      setEditOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to update draft: ${formatApiError(error)}`);
    }
  };

  const handleDeleteDraft = async (draft: DraftBoard) => {
    if (!window.confirm(`Delete draft "${draft.name}" and its draftees/assignments?`)) return;
    try {
      setErrorMessage("");
      for (const assignment of assignments.filter((item) => item.draftBoardId === draft.id)) {
        await API.graphql(graphqlOperation(deleteDraftAssignmentMutation, { input: { id: assignment.id } }));
      }
      for (const draftee of draftees.filter((item) => item.draftBoardId === draft.id)) {
        await API.graphql(graphqlOperation(deleteDrafteeMutation, { input: { id: draftee.id } }));
      }
      await API.graphql(graphqlOperation(deleteDraftBoardMutation, { input: { id: draft.id } }));
      if (activeDraftId === draft.id) setActiveDraftId("");
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to delete draft: ${formatApiError(error)}`);
    }
  };

  const handleAssignDraftee = async (teamId: string, drafteeId: string) => {
    if (!activeDraft) return;
    const exists = activeAssignments.find((assignment) => assignment.teamId === teamId && assignment.drafteeId === drafteeId);
    if (exists) return;
    try {
      setErrorMessage("");
      setDraggingOverTeamId(null);
      for (const assignment of activeAssignments.filter((item) => item.drafteeId === drafteeId)) {
        await API.graphql(graphqlOperation(deleteDraftAssignmentMutation, { input: { id: assignment.id } }));
      }
      await API.graphql(graphqlOperation(createDraftAssignmentMutation, { input: { draftBoardId: activeDraft.id, teamId, drafteeId } }));
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to assign draftee: ${formatApiError(error)}`);
    }
  };

  const handleRemoveAssignment = async (assignment: DraftAssignment) => {
    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(deleteDraftAssignmentMutation, { input: { id: assignment.id } }));
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to remove assignment: ${formatApiError(error)}`);
    }
  };

  const showDrafteeHover = (drafteeId: string, element: HTMLDivElement | null) => {
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const hoverWidth = 250;
    const gutter = 14;
    const viewportPadding = 16;
    const placeLeft = rect.right + gutter + hoverWidth > window.innerWidth - viewportPadding;
    const left = placeLeft
      ? Math.max(viewportPadding, rect.left - hoverWidth - gutter)
      : Math.min(window.innerWidth - hoverWidth - viewportPadding, rect.right + gutter);
    const top = Math.min(
      window.innerHeight - 120,
      Math.max(90, rect.top + rect.height / 2)
    );
    setHoveredDraftee({
      drafteeId,
      top,
      left,
      placement: placeLeft ? "left" : "right"
    });
  };

  const clearDrafteeHover = () => {
    setHoveredDraftee((current) => (current ? null : current));
  };

  const handleDeleteDraftee = async (draftee: Draftee) => {
    if (!window.confirm(`Delete draftee ${drafteeFullName(draftee)}?`)) return;
    try {
      setErrorMessage("");
      for (const assignment of activeAssignments.filter((item) => item.drafteeId === draftee.id)) {
        await API.graphql(graphqlOperation(deleteDraftAssignmentMutation, { input: { id: assignment.id } }));
      }
      await API.graphql(graphqlOperation(deleteDrafteeMutation, { input: { id: draftee.id } }));
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to delete draftee: ${formatApiError(error)}`);
    }
  };

  const handleBulkDrafteeUpload = async (file: File) => {
    if (!activeDraft) {
      setBulkErrorMessage("Select a draft board before uploading draftees.");
      return;
    }
    setBulkLoading(true);
    setBulkErrorMessage("");
    setBulkStatusMessage("");
    try {
      const raw = await file.text();
      const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      if (lines.length < 2) throw new Error("CSV must include a header row and at least one data row.");
      const headers = splitCsvLine(lines[0]).map(normalizeHeader);
      const headerMap: Record<string, number> = {};
      headers.forEach((header, index) => { headerMap[header] = index; });
      const getCell = (cells: string[], aliases: string[]) => {
        for (const alias of aliases) {
          const idx = headerMap[alias];
          if (typeof idx === "number") return (cells[idx] ?? "").trim();
        }
        return "";
      };
      const missingRequired = ["firstname", "lastname", "dateofbirth", "town", "state", "parentemail"].filter((field) => typeof headerMap[field] !== "number");
      if (missingRequired.length > 0) {
        throw new Error(`CSV is missing required columns: ${missingRequired.join(", ")}.`);
      }

      let createdCount = 0;
      const rowErrors: string[] = [];
      for (let i = 1; i < lines.length; i += 1) {
        const row = splitCsvLine(lines[i]);
        const rowNumber = i + 1;
        const firstName = getCell(row, ["firstname", "first"]);
        const lastName = getCell(row, ["lastname", "last"]);
        const rawDateOfBirth = getCell(row, ["dateofbirth", "dob", "birthdate"]);
        const dateOfBirth = normalizeDateForApi(rawDateOfBirth);
        const town = getCell(row, ["town", "city"]);
        const state = getCell(row, ["state"]);
        const parentEmail = getCell(row, ["parentemail", "email"]);
        if (!firstName || !lastName || !rawDateOfBirth || !town || !state || !parentEmail) {
          rowErrors.push(`Row ${rowNumber}: missing one or more required values.`);
          continue;
        }
        if (!dateOfBirth) {
          rowErrors.push(`Row ${rowNumber}: invalid date of birth "${rawDateOfBirth}".`);
          continue;
        }
        try {
          await API.graphql(graphqlOperation(createDrafteeMutation, {
            input: {
              draftBoardId: activeDraft.id,
              firstName,
              lastName,
              dateOfBirth,
              town,
              state: state.toUpperCase(),
              parentEmail: parentEmail.toLowerCase(),
              batting: coerceOptionalInt(getCell(row, ["batting"])),
              fielding: coerceOptionalInt(getCell(row, ["fielding"])),
              throwing: coerceOptionalInt(getCell(row, ["throwing"])),
              baserunning: coerceOptionalInt(getCell(row, ["baserunning"])),
              coachability: coerceOptionalInt(getCell(row, ["coachability"])),
              availabilityRating: coerceOptionalInt(getCell(row, ["availabilityrating", "availability"])),
              catcher: coerceBoolean(getCell(row, ["catcher"])),
              pitcher: coerceBoolean(getCell(row, ["pitcher"]))
            }
          }));
          createdCount += 1;
        } catch (error) {
          rowErrors.push(`Row ${rowNumber}: ${formatApiError(error)}`);
        }
      }
      await loadData(true);
      if (rowErrors.length > 0) setBulkErrorMessage(rowErrors.slice(0, 12).join(" | "));
      setBulkStatusMessage(`Draft upload complete. Created ${createdCount} draftee${createdCount === 1 ? "" : "s"}${rowErrors.length ? `, ${rowErrors.length} row error(s)` : ""}.`);
    } catch (error) {
      setBulkErrorMessage(`Draft upload failed: ${formatApiError(error)}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleFinalizeDraft = async () => {
    if (!activeDraft) return;
    setFinalizingDraft(true);
    setErrorMessage("");
    try {
      const existingMembershipKeys = new Set(rosterMemberships.map((item) => `${item.playerId}|${item.teamId}|${item.seasonId}`));
      const existingTeamPlayerKeys = new Set(teamPlayers.map((item) => `${item.playerID}|${item.teamID}`));
      let createdPlayersCount = 0;
      let reusedPlayersCount = 0;
      for (const draftee of activeDraftees) {
        if (draftee.finalizedPlayerId) continue;
        const drafteeAssignments = activeAssignments.filter((item) => item.drafteeId === draftee.id);
        if (drafteeAssignments.length === 0) continue;
        const primaryAssignment = drafteeAssignments[0];
        const finalizePlayerResponse: any = await API.post("AdminQueries", "/drafts/finalizePlayer", {
          body: {
            name: drafteeFullName(draftee),
            teamId: primaryAssignment.teamId,
            town: draftee.town || null,
            state: draftee.state || null,
            dateOfBirth: draftee.dateOfBirth,
            parentEmail: draftee.parentEmail?.trim() || null
          }
        });
        const playerId = finalizePlayerResponse?.playerId as string | undefined;
        if (!playerId) throw new Error(`Failed to create player for ${drafteeFullName(draftee)}.`);
        if (finalizePlayerResponse?.wasDuplicate) {
          reusedPlayersCount += 1;
        } else {
          createdPlayersCount += 1;
        }
        for (const assignment of drafteeAssignments) {
          const membershipKey = `${playerId}|${assignment.teamId}|${activeDraft.seasonId}`;
          if (!existingMembershipKeys.has(membershipKey)) {
            await API.graphql(graphqlOperation(createRosterMembershipMutation, { input: { playerId, teamId: assignment.teamId, seasonId: activeDraft.seasonId, organizationId: activeDraft.organizationId, programId: activeDraft.programId || null, relationship: "DRAFTED", isPrimary: assignment.teamId === primaryAssignment.teamId } }));
            existingMembershipKeys.add(membershipKey);
          }
          if (assignment.teamId !== primaryAssignment.teamId) {
            const teamPlayerKey = `${playerId}|${assignment.teamId}`;
            if (!existingTeamPlayerKeys.has(teamPlayerKey)) {
              await API.graphql(graphqlOperation(createTeamPlayerMutation, { input: { teamID: assignment.teamId, playerID: playerId } }));
              existingTeamPlayerKeys.add(teamPlayerKey);
            }
          }
        }
        await API.graphql(graphqlOperation(updateDrafteeMutation, { input: { id: draftee.id, finalizedPlayerId: playerId } }));
      }
      await API.graphql(graphqlOperation(updateDraftBoardMutation, { input: { id: activeDraft.id, status: "COMPLETE" } }));
      await loadData(true);
      setBulkStatusMessage(`Draft finalized. ${createdPlayersCount} players created, ${reusedPlayersCount} matched existing players, and all assignments were linked to their teams.`);
    } catch (error) {
      setErrorMessage(`Failed to finalize draft: ${formatApiError(error)}`);
    } finally {
      setFinalizingDraft(false);
    }
  };

  const handleAutoDraft = async () => {
    if (!activeDraft) {
      return;
    }
    if (eligibleTeams.length === 0) {
      setErrorMessage("No eligible teams are available for this draft board.");
      return;
    }
    const draftableDraftees = activeDraftees.filter((draftee) => !draftee.finalizedPlayerId);
    if (draftableDraftees.length === 0) {
      setErrorMessage("There are no unfinalized draftees available to auto-draft.");
      return;
    }

    setAutoDraftLoading(true);
    setErrorMessage("");
    setBulkStatusMessage("");
    try {
      const teamLoadMap = eligibleTeams.reduce<Record<string, TeamDraftLoad>>((acc, team) => {
        acc[team.id] = createInitialTeamLoad(team.id);
        return acc;
      }, {});

      const finalizedAssignments = activeAssignments.filter((assignment) => drafteeById[assignment.drafteeId]?.finalizedPlayerId);
      for (const assignment of finalizedAssignments) {
        const draftee = drafteeById[assignment.drafteeId];
        const load = teamLoadMap[assignment.teamId];
        if (!draftee || !load) {
          continue;
        }
        applyDrafteeToLoad(load, draftee);
      }

      const assignmentsToReplace = activeAssignments.filter((assignment) => !drafteeById[assignment.drafteeId]?.finalizedPlayerId);
      const availableDraftees = draftableDraftees;
      const tieBreaker = eligibleTeams.reduce<Record<string, number>>((acc, team, index) => {
        acc[team.id] = index / 1000;
        return acc;
      }, {});

      let generatedPlan: Array<{ teamId: string; drafteeId: string }> = [];

      if (autoDraftMode === "BALANCE") {
        generatedPlan = distributeBalancedAssignments(availableDraftees, eligibleTeams.map((team) => teamLoadMap[team.id] ?? createInitialTeamLoad(team.id)), tieBreaker);
      } else {
        const teamsByTier = tierRankOrder.reduce<Record<TierRank, Team[]>>((acc, tier) => {
          acc[tier] = eligibleTeams.filter((team) => normalizeTierRank(teamTierRanks[team.id]) === tier);
          return acc;
        }, { A: [], B: [], C: [], D: [] });
        generatedPlan = distributeTieredAssignments(availableDraftees, eligibleTeams.map((team) => teamLoadMap[team.id] ?? createInitialTeamLoad(team.id)), teamsByTier);
      }

      for (const assignment of assignmentsToReplace) {
        await API.graphql(graphqlOperation(deleteDraftAssignmentMutation, { input: { id: assignment.id } }));
      }
      for (const planItem of generatedPlan) {
        await API.graphql(graphqlOperation(createDraftAssignmentMutation, { input: { draftBoardId: activeDraft.id, teamId: planItem.teamId, drafteeId: planItem.drafteeId } }));
      }

      await loadData(true);
      setShowAutoDraftDialog(false);
      setBulkStatusMessage(`Auto draft complete. ${generatedPlan.length} draftee${generatedPlan.length === 1 ? "" : "s"} assigned using ${autoDraftMode === "BALANCE" ? "balanced teams" : "tiered"} mode.`);
    } catch (error) {
      setErrorMessage(`Failed to auto draft: ${formatApiError(error)}`);
    } finally {
      setAutoDraftLoading(false);
    }
  };

  const handleResetDraft = async () => {
    if (!activeDraft) {
      return;
    }
    const hasFinalizedAssignments = activeAssignments.some((assignment) => drafteeById[assignment.drafteeId]?.finalizedPlayerId);
    const confirmMessage = hasFinalizedAssignments
      ? `Reset assignments for "${activeDraft.name}"? Finalized players will remain in the database, but this board's team assignments will be cleared.`
      : `Reset assignments for "${activeDraft.name}"? This clears all current team assignments on the board.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      setErrorMessage("");
      setBulkStatusMessage("");
      for (const assignment of activeAssignments) {
        await API.graphql(graphqlOperation(deleteDraftAssignmentMutation, { input: { id: assignment.id } }));
      }
      await API.graphql(graphqlOperation(updateDraftBoardMutation, { input: { id: activeDraft.id, status: "OPEN" } }));
      await loadData(true);
      setBulkStatusMessage(`Draft reset complete. ${activeAssignments.length} assignment${activeAssignments.length === 1 ? "" : "s"} removed.`);
    } catch (error) {
      setErrorMessage(`Failed to reset draft: ${formatApiError(error)}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Draft Manager</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {canManage && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={() => setCreateOpen(true)}>Add Draft Board</button>
          <button className="bg-green-600 text-white px-4 py-2 rounded" onClick={() => setShowUploadDialog(true)} disabled={!activeDraft}>Upload Draftees CSV</button>
          <button className="bg-green-600 text-white px-4 py-2 rounded" onClick={() => setShowAutoDraftDialog(true)} disabled={!activeDraft || eligibleTeams.length === 0 || activeDraftees.filter((item) => !item.finalizedPlayerId).length === 0}>Auto Draft</button>
          <button className="bg-yellow-500 text-white px-4 py-2 rounded" onClick={handleFinalizeDraft} disabled={!activeDraft || finalizingDraft || unfinalizedAssignedCount === 0}>{finalizingDraft ? "Finalizing..." : "Finalize To Players"}</button>
          <button className="bg-red-600 text-white px-4 py-2 rounded" onClick={handleResetDraft} disabled={!activeDraft || activeAssignments.length === 0}>Reset Draft</button>
          <button className="bg-yellow-500 text-white px-4 py-2 rounded" onClick={() => loadData()}>Refresh</button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input value={filterText} onChange={(event) => setFilterText(event.target.value)} className="border px-3 py-2 rounded" placeholder="Filter drafts" />
        <select value={filterOrganizationId} onChange={(event) => setFilterOrganizationId(event.target.value)} className="border px-3 py-2 rounded"><option value="">All organizations</option>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}</select>
        <select value={filterSeasonId} onChange={(event) => setFilterSeasonId(event.target.value)} className="border px-3 py-2 rounded"><option value="">All seasons</option>{seasons.map((season) => <option key={season.id} value={season.id}>{season.name} {season.year}</option>)}</select>
        <select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)} className="border px-3 py-2 rounded"><option value="">All statuses</option><option value="PLANNING">PLANNING</option><option value="OPEN">OPEN</option><option value="LOCKED">LOCKED</option><option value="COMPLETE">COMPLETE</option></select>
      </div>

      <div className="bubble-table-wrapper mb-4">
        <table className="bubble-table">
          <thead><tr><th><button className="table-sort" onClick={() => toggleSort("name")}>Draft</button></th><th><button className="table-sort" onClick={() => toggleSort("organization")}>Organization</button></th><th><button className="table-sort" onClick={() => toggleSort("program")}>Program</button></th><th><button className="table-sort" onClick={() => toggleSort("season")}>Season</button></th><th><button className="table-sort" onClick={() => toggleSort("ageGroup")}>Age Group</button></th><th><button className="table-sort" onClick={() => toggleSort("status")}>Status</button></th><th>Draftees</th>{canManage && <th>Actions</th>}</tr></thead>
          <tbody>
            {filteredDrafts.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="text-gray-600">No draft boards found.</td></tr>}
            {filteredDrafts.map((draft) => <tr key={draft.id} onClick={() => setActiveDraftId(draft.id)} style={draft.id === activeDraftId ? { background: "rgba(215, 38, 61, 0.08)" } : undefined}><td>{draft.name}</td><td>{organizationNameById[draft.organizationId] ?? "Unknown org"}</td><td>{draft.programId ? (programNameById[draft.programId] ?? "Unknown program") : "Any"}</td><td>{seasonLabelById[draft.seasonId] ?? "Unknown season"}</td><td>{draft.ageGroup?.trim() || "Any"}</td><td>{draft.status}</td><td>{draftees.filter((item) => item.draftBoardId === draft.id).length}</td>{canManage && <td><div className="flex gap-2"><button className="bg-yellow-500 text-white px-3 py-1 rounded" onClick={(event) => { event.stopPropagation(); setEditingDraftId(draft.id); setEditingName(draft.name); setEditingStatus(draft.status); setEditingAgeGroup(draft.ageGroup ?? ""); setEditingNotes(draft.notes ?? ""); setEditingOrganizationId(draft.organizationId); setEditingSeasonId(draft.seasonId); setEditingProgramId(draft.programId ?? ""); setEditOpen(true); }}>Edit</button><button className="bg-red-600 text-white px-3 py-1 rounded" onClick={(event) => { event.stopPropagation(); handleDeleteDraft(draft); }}>Delete</button></div></td>}</tr>)}
          </tbody>
        </table>
      </div>

      {activeDraft && (
        <>
          <div className="bubble-card p-4 mb-4">
            <div className="flex justify-between items-center gap-2 mb-2">
              <div>
                <h2 className="text-lg font-bold mb-1">{activeDraft.name}</h2>
                <div className="bubble-pill-wrap">
                  <span className="bubble-pill">{organizationNameById[activeDraft.organizationId] ?? "Unknown org"}</span>
                  <span className="bubble-pill">{seasonLabelById[activeDraft.seasonId] ?? "Unknown season"}</span>
                  <span className="bubble-pill">{activeDraft.programId ? (programNameById[activeDraft.programId] ?? "Unknown program") : "Any program"}</span>
                  <span className="bubble-pill">{activeDraft.ageGroup?.trim() || "Any age group"}</span>
                  <span className="bubble-pill">{activeDraft.status}</span>
                  <span className="bubble-pill">{activeDraftees.length} draftees</span>
                  <span className="bubble-pill">{finalizedCount} finalized</span>
                </div>
              </div>
              <div className="text-sm text-gray-600">{activeAssignments.length} assignments</div>
            </div>
            {activeDraft.notes?.trim() && <p className="text-sm text-gray-600 mb-2">{activeDraft.notes}</p>}
            {bulkErrorMessage && <p className="mb-2" style={{ color: "#c1121f" }}>{bulkErrorMessage}</p>}
            {bulkStatusMessage && <p className="mb-0" style={{ color: "#166534" }}>{bulkStatusMessage}</p>}
          </div>

          <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
            <input value={poolSearch} onChange={(event) => setPoolSearch(event.target.value)} className="border px-3 py-2 rounded" placeholder="Find draftees" />
            <select value={poolAgeGroup} onChange={(event) => setPoolAgeGroup(event.target.value)} className="border px-3 py-2 rounded"><option value="">All ages</option>{Array.from(new Set(activeDraftees.map((item) => computeAgeFromDob(item.dateOfBirth)).filter((value): value is number => value !== null))).sort((a, b) => a - b).map((age) => <option key={age} value={`${age}`}>{age}</option>)}</select>
            <select value={drafteeSortKey} onChange={(event) => toggleDrafteeSort(event.target.value as DrafteeSortKey)} className="border px-3 py-2 rounded"><option value="name">Sort: Name</option><option value="dob">Sort: DOB</option><option value="town">Sort: Town</option><option value="state">Sort: State</option><option value="availability">Sort: Availability</option></select>
          </div>

          <div className="draft-layout">
            <section className="bubble-card p-4 draft-pool">
              <div className="flex justify-between items-center mb-2"><h2 className="text-lg font-bold">Draftee Pool</h2><span className="text-sm text-gray-600">{poolDraftees.length} draftees</span></div>
              <div className="draft-pool-list">
                {poolDraftees.map((draftee) => (
                  <div
                    key={draftee.id}
                    className="draft-player-card draft-player-card--compact"
                    draggable={canManage && !draftee.finalizedPlayerId}
                    onMouseEnter={(event) => showDrafteeHover(draftee.id, event.currentTarget)}
                    onMouseLeave={clearDrafteeHover}
                    onDragStart={(event) => {
                      setDraggingDrafteeId(draftee.id);
                      setHoveredDraftee(null);
                      event.dataTransfer.setData("text/plain", draftee.id);
                      event.dataTransfer.setData("application/x-draftee-id", draftee.id);
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => {
                      setDraggingDrafteeId(null);
                      setDraggingOverTeamId(null);
                    }}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <div className="draft-player-card__name">{drafteeFullName(draftee)}</div>
                      {canManage && !draftee.finalizedPlayerId && <button className="draft-team-assignment__remove" onClick={() => handleDeleteDraftee(draftee)} aria-label={`Delete ${drafteeFullName(draftee)}`}>x</button>}
                    </div>
                    <div className="bubble-pill-wrap">
                      {draftee.pitcher ? <span className="bubble-pill">P</span> : null}
                      {draftee.catcher ? <span className="bubble-pill">C</span> : null}
                    </div>
                  </div>
                ))}
                {poolDraftees.length === 0 && <div className="text-sm text-gray-600">No draftees match the current criteria.</div>}
              </div>
            </section>

            <section className="draft-board-columns">
              {eligibleTeams.map((team) => {
                const teamRows = teamAssignments[team.id] ?? [];
                const teamPitcherCount = teamRows.reduce((count, assignment) => count + (drafteeById[assignment.drafteeId]?.pitcher ? 1 : 0), 0);
                const teamCatcherCount = teamRows.reduce((count, assignment) => count + (drafteeById[assignment.drafteeId]?.catcher ? 1 : 0), 0);
                const teamDraftees = teamRows.map((assignment) => drafteeById[assignment.drafteeId]).filter((item): item is Draftee => Boolean(item));
                const battingAverage = averageCategoryForDraftees(teamDraftees, (item) => item.batting);
                const fieldingAverage = averageCategoryForDraftees(teamDraftees, (item) => item.fielding);
                const throwingAverage = averageCategoryForDraftees(teamDraftees, (item) => item.throwing);
                const baserunningAverage = averageCategoryForDraftees(teamDraftees, (item) => item.baserunning);
                const coachabilityAverage = averageCategoryForDraftees(teamDraftees, (item) => item.coachability);
                const availabilityAverage = averageCategoryForDraftees(teamDraftees, (item) => item.availabilityRating);
                const formatAverage = (value: number | null) => value === null ? "-" : value.toFixed(1);
                return <div key={team.id} className={`bubble-card p-4 draft-team-column ${draggingOverTeamId === team.id ? "draft-team-column--active" : ""}`}><div className="flex justify-between items-center mb-2 gap-2"><div><div className="draft-team-column__title">{team.name}</div><div className="draft-team-column__meta">{team.ageGroup?.trim() || "No age group"}</div><div className="bubble-pill-wrap"><span className="bubble-pill">{teamRows.length} players</span><span className="bubble-pill">{teamPitcherCount} pitchers</span><span className="bubble-pill">{teamCatcherCount} catchers</span></div><div className="draft-team-column__averages">Bat {formatAverage(battingAverage)} • Fld {formatAverage(fieldingAverage)} • Thr {formatAverage(throwingAverage)} • Run {formatAverage(baserunningAverage)} • Coach {formatAverage(coachabilityAverage)} • Avail {formatAverage(availabilityAverage)}</div></div></div><div className="draft-team-column__list" onDragEnter={(event) => { if (!canManage || !draggingDrafteeId) return; event.preventDefault(); setDraggingOverTeamId(team.id); }} onDragOver={(event) => { if (!canManage || !draggingDrafteeId) return; event.preventDefault(); event.dataTransfer.dropEffect = "move"; if (draggingOverTeamId !== team.id) { setDraggingOverTeamId(team.id); } }} onDragLeave={(event) => { if (!canManage) return; const relatedTarget = event.relatedTarget as Node | null; if (relatedTarget && event.currentTarget.contains(relatedTarget)) { return; } if (draggingOverTeamId === team.id) { setDraggingOverTeamId(null); } }} onDrop={(event) => { if (!canManage) return; event.preventDefault(); const drafteeId = event.dataTransfer.getData("application/x-draftee-id") || event.dataTransfer.getData("text/plain") || draggingDrafteeId; setDraggingDrafteeId(null); setDraggingOverTeamId(null); if (drafteeId) { void handleAssignDraftee(team.id, drafteeId); } }}>{teamRows.map((assignment) => { const draftee = drafteeById[assignment.drafteeId]; if (!draftee) return null; return <div key={assignment.id} className="draft-team-assignment draft-player-card draft-player-card--compact" onMouseEnter={(event) => showDrafteeHover(draftee.id, event.currentTarget)} onMouseLeave={clearDrafteeHover}><div className="flex justify-between items-center gap-2"><div><div className="draft-player-card__name">{drafteeFullName(draftee)}</div><div className="bubble-pill-wrap">{draftee.pitcher ? <span className="bubble-pill">P</span> : null}{draftee.catcher ? <span className="bubble-pill">C</span> : null}</div><div className="draft-player-card__meta">DOB {draftee.dateOfBirth} • Age {computeAgeFromDob(draftee.dateOfBirth) ?? "-"} • {draftee.town ?? "-"}, {draftee.state ?? "-"}</div></div>{canManage && <button className="draft-team-assignment__remove" onClick={() => handleRemoveAssignment(assignment)} aria-label={`Remove ${drafteeFullName(draftee)} from ${team.name}`}>x</button>}</div></div>; })}{teamRows.length === 0 && <div className="draft-drop-hint">Drag draftees here to assign them to this team.</div>}</div></div>;
              })}
              {eligibleTeams.length === 0 && <div className="bubble-card p-4 text-sm text-gray-600">No teams match this draft board yet. Make sure the organization, season, program, and age group line up with real teams.</div>}
            </section>
          </div>
          {hoveredDraftee && drafteeById[hoveredDraftee.drafteeId] && (
            <div
              className={`draft-hover-card draft-hover-card--floating draft-hover-card--${hoveredDraftee.placement}`}
              style={{ top: hoveredDraftee.top, left: hoveredDraftee.left }}
            >
              <div className="draft-hover-card__title">{drafteeFullName(drafteeById[hoveredDraftee.drafteeId])}</div>
              <div className="draft-hover-card__row">DOB {drafteeById[hoveredDraftee.drafteeId].dateOfBirth}</div>
              <div className="draft-hover-card__row">Age {computeAgeFromDob(drafteeById[hoveredDraftee.drafteeId].dateOfBirth) ?? "-"}</div>
              <div className="draft-hover-card__row">{drafteeById[hoveredDraftee.drafteeId].town ?? "-"}, {drafteeById[hoveredDraftee.drafteeId].state ?? "-"}</div>
              <div className="draft-hover-card__row">{drafteeById[hoveredDraftee.drafteeId].parentEmail ?? "No parent email"}</div>
              <div className="draft-hover-card__grid">
                <span>Bat {drafteeById[hoveredDraftee.drafteeId].batting ?? "-"}</span>
                <span>Fld {drafteeById[hoveredDraftee.drafteeId].fielding ?? "-"}</span>
                <span>Thr {drafteeById[hoveredDraftee.drafteeId].throwing ?? "-"}</span>
                <span>Run {drafteeById[hoveredDraftee.drafteeId].baserunning ?? "-"}</span>
                <span>Coach {drafteeById[hoveredDraftee.drafteeId].coachability ?? "-"}</span>
                <span>Avail {drafteeById[hoveredDraftee.drafteeId].availabilityRating ?? "-"}</span>
              </div>
            </div>
          )}
        </>
      )}

      <AppDialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} title="Upload Draftees CSV" footer={<button onClick={() => setShowUploadDialog(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Close</button>}>
        <p className="text-sm text-gray-600 mb-2">Required columns: <code>First Name</code>, <code>Last Name</code>, <code>Date of Birth</code>, <code>Town</code>, <code>State</code>, <code>Parent Email</code>.</p>
        <p className="text-sm text-gray-600 mb-2">Optional columns: <code>Batting</code>, <code>Fielding</code>, <code>Throwing</code>, <code>Baserunning</code>, <code>Coachability</code>, <code>Availability Rating</code>, <code>Catcher</code>, <code>Pitcher</code>.</p>
        <p className="text-sm mb-2"><a href="/sample-draftees-upload.csv" download>Download Sample CSV</a></p>
        {bulkErrorMessage && <p className="mb-2" style={{ color: "#c1121f" }}>{bulkErrorMessage}</p>}
        {bulkStatusMessage && <p className="mb-2" style={{ color: "#166534" }}>{bulkStatusMessage}</p>}
        <input type="file" accept=".csv,text/csv" className="border px-3 py-2 rounded" disabled={bulkLoading} onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; await handleBulkDrafteeUpload(file); event.currentTarget.value = ""; }} />
      </AppDialog>

      <AppDialog open={showAutoDraftDialog} onClose={() => setShowAutoDraftDialog(false)} title="Auto Draft" footer={<><button onClick={() => setShowAutoDraftDialog(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button><button onClick={handleAutoDraft} className="bg-blue-600 text-white px-3 py-2 rounded" disabled={autoDraftLoading || !activeDraft}>{autoDraftLoading ? "Assigning..." : "Run Auto Draft"}</button></>}>
        <div className="space-y-3">
          <p className="text-sm text-gray-600">This replaces current unfinalized draft assignments for the selected board. Finalized player assignments stay in place and are used as the baseline when balancing.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <select value={autoDraftMode} onChange={(event) => setAutoDraftMode(event.target.value as AutoDraftMode)} className="border px-3 py-2 rounded">
              <option value="BALANCE">Balance Teams</option>
              <option value="TIERED">Tiered</option>
            </select>
            <div className="text-sm text-gray-600 px-3 py-2 border rounded bg-white">
              {eligibleTeams.length} teams • {activeDraftees.filter((item) => !item.finalizedPlayerId).length} available draftees
            </div>
          </div>
          {autoDraftMode === "BALANCE" ? (
            <p className="text-sm text-gray-600">Balanced mode uses overall player ratings first, then evens out team size, pitcher count, and catcher count.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Tiered mode assigns the top share of players to `A`, next share to `B`, then `C` and `D`. It also spreads pitchers and catchers first, aiming for at least 2 of each per team when enough eligible players exist, with `A` getting slightly more depth than `B`, then `C`, then `D`.</p>
              <div className="bubble-table-wrapper">
                <table className="bubble-table">
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Current Age Group</th>
                      <th>Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleTeams.map((team) => (
                      <tr key={team.id}>
                        <td>{team.name}</td>
                        <td>{team.ageGroup?.trim() || "No age group"}</td>
                        <td>
                          <select value={teamTierRanks[team.id] ?? "B"} onChange={(event) => setTeamTierRanks((current) => ({ ...current, [team.id]: normalizeTierRank(event.target.value) }))} className="border px-3 py-2 rounded">
                            {tierRankOrder.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </AppDialog>

      <AppDialog open={createOpen} onClose={() => setCreateOpen(false)} title="Add Draft Board" footer={<><button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button><button onClick={handleCreateDraft} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button></>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={newName} onChange={(event) => setNewName(event.target.value)} className="border px-3 py-2 rounded" placeholder="Draft board name" />
          <select value={newStatus} onChange={(event) => setNewStatus(event.target.value)} className="border px-3 py-2 rounded"><option value="PLANNING">PLANNING</option><option value="OPEN">OPEN</option><option value="LOCKED">LOCKED</option><option value="COMPLETE">COMPLETE</option></select>
          <select value={newOrganizationId} onChange={(event) => { setNewOrganizationId(event.target.value); setNewProgramId(""); }} className="border px-3 py-2 rounded"><option value="">Select organization</option>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}</select>
          <select value={newSeasonId} onChange={(event) => setNewSeasonId(event.target.value)} className="border px-3 py-2 rounded"><option value="">Select season</option>{seasons.map((season) => <option key={season.id} value={season.id}>{season.name} {season.year}</option>)}</select>
          <select value={newProgramId} onChange={(event) => setNewProgramId(event.target.value)} className="border px-3 py-2 rounded"><option value="">Any program</option>{currentPrograms.map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}</select>
          <input value={newAgeGroup} onChange={(event) => setNewAgeGroup(event.target.value)} className="border px-3 py-2 rounded" placeholder="Age group, e.g. 11U" />
          <input value={newNotes} onChange={(event) => setNewNotes(event.target.value)} className="border px-3 py-2 rounded md:col-span-2" placeholder="Notes" />
        </div>
      </AppDialog>

      <AppDialog open={editOpen} onClose={() => setEditOpen(false)} title="Edit Draft Board" footer={<><button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button><button onClick={handleUpdateDraft} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button></>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={editingName} onChange={(event) => setEditingName(event.target.value)} className="border px-3 py-2 rounded" placeholder="Draft board name" />
          <select value={editingStatus} onChange={(event) => setEditingStatus(event.target.value)} className="border px-3 py-2 rounded"><option value="PLANNING">PLANNING</option><option value="OPEN">OPEN</option><option value="LOCKED">LOCKED</option><option value="COMPLETE">COMPLETE</option></select>
          <select value={editingOrganizationId} onChange={(event) => { setEditingOrganizationId(event.target.value); setEditingProgramId(""); }} className="border px-3 py-2 rounded"><option value="">Select organization</option>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}</select>
          <select value={editingSeasonId} onChange={(event) => setEditingSeasonId(event.target.value)} className="border px-3 py-2 rounded"><option value="">Select season</option>{seasons.map((season) => <option key={season.id} value={season.id}>{season.name} {season.year}</option>)}</select>
          <select value={editingProgramId} onChange={(event) => setEditingProgramId(event.target.value)} className="border px-3 py-2 rounded"><option value="">Any program</option>{currentPrograms.map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}</select>
          <input value={editingAgeGroup} onChange={(event) => setEditingAgeGroup(event.target.value)} className="border px-3 py-2 rounded" placeholder="Age group, e.g. 11U" />
          <input value={editingNotes} onChange={(event) => setEditingNotes(event.target.value)} className="border px-3 py-2 rounded md:col-span-2" placeholder="Notes" />
        </div>
      </AppDialog>
    </div>
  );
};

export default DraftsPage;








